import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';
import {
  MoviefoneApiResponse,
  MoviefoneMispricedOption,
  MoviefoneStrategy,
  MoviefoneCatalogEntry,
  MoviefoneForwardVolPair,
  MoviefoneCandidateTable,
  SkewPlotResponse,
  DollarValueSummary,
  MoviefoneCorrelationMetrics,
} from 'types/moviefone';
import VolatilityChartPanel from './VolatilityChartPanel';
import DollarValueChart3D from './DollarValueChart3D';
import MoviefoneStrategyCard from './MoviefoneStrategyCard';
import { KVTable, MiniTable, Section, TVTPRegimeBars } from './MoviefoneShared';
import { TOOL_DEFINITIONS } from 'util/toolCalling';
import { getGreeksColor, num, pct, pct0, usd } from 'util/moviefoneFormat';
import { MoviefoneAnalysis, SearchCandidate } from 'gen/stream-api/requests';

const getEMA = (values: number[], windowSize: number = 17): number[] => {
  const k = 2 / (windowSize + 1);
  const ema: number[] = [];
  ema[0] = values[0];
  for (let i = 1; i < values.length; i++) {
    ema[i] = values[i] * k + ema[i - 1] * (1 - k);
  }
  return ema;
};

const getZScoreColor = (z: number, theme: Theme): string => {
  if (z > 0.5) {
    return theme.palette.success.main;
  }
  if (z > 0.2) {
    return theme.palette.success.light;
  }
  if (z < -0.5) {
    return theme.palette.error.main;
  }
  if (z < -0.2) {
    return theme.palette.error.light;
  }
  return theme.palette.text.primary;
};

const getScoreColor = (score: number, theme: Theme): string => {
  if (score >= 0.8) {
    return theme.palette.success.dark;
  }
  if (score >= 0.6) {
    return theme.palette.success.main;
  }
  if (score >= 0.4) {
    return theme.palette.warning.dark;
  }
  if (score >= 0.2) {
    return theme.palette.warning.main;
  }
  return theme.palette.text.primary;
};

const getVRPColor = (vrp: number, theme: Theme): string => {
  if (vrp > 0) {
    return theme.palette.success.main;
  }
  if (vrp < 0) {
    return theme.palette.error.main;
  }
  return theme.palette.text.secondary;
};

const getCarryColor = (quality: string, theme: Theme): string => {
  if (quality === 'HIGH') {
    return theme.palette.success.main;
  }
  if (quality === 'LOW') {
    return theme.palette.error.main;
  }
  return theme.palette.warning.main;
};

import { Theme } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { Tabs } from 'components/shared';
import { useState } from 'react';

interface MoviefoneAdvisorProps {
  data: MoviefoneApiResponse;
  onAsk?: (msg: string) => void;
}

const SkewPlotSection = ({ skewData }: { skewData: SkewPlotResponse }) => {
  const theme = useTheme();

  const formatIV = (v: number) => `${(v * 100).toFixed(1)}%`;

  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  const spot = skewData.expirations[0]?.spot ?? 0;

  // Only show the curve with the largest DTE
  const maxDteExp = skewData.expirations.reduce(
    (a, b) => (a.dte > b.dte ? a : b),
    skewData.expirations[0],
  );
  const expirationsEMA = [maxDteExp].map((exp) => ({
    ...exp,
    ivs: getEMA(exp.ivs),
  }));

  const combinedData =
    expirationsEMA[0]?.strikes.map((strike, i) => {
      const point: Record<string, number> = { strike };
      expirationsEMA.forEach((exp) => {
        point[`iv_${exp.dte}`] = exp.ivs[i];
      });
      return point;
    }) ?? [];

  // Use only the largest DTE expiration for domain calculations
  const allStrikes = maxDteExp.strikes;
  const fwdStrikes = skewData.forward_vol_data?.strikes ?? [];
  const allIVs = maxDteExp.ivs;
  const xDomain = [
    Math.min(...allStrikes, ...fwdStrikes),
    Math.max(...allStrikes, ...fwdStrikes),
  ];
  const yDomain = [
    Math.min(...allIVs) - 0.02,
    Math.min(Math.max(...allIVs) + 0.02, 4.0),
  ];

  return (
    <Stack sx={{ gap: 1 }}>
      <Box sx={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={combinedData}
            margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="strike"
              type="number"
              tick={{ fontSize: 10 }}
              domain={xDomain}
              tickFormatter={(v: number) => v.toFixed(0)}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickFormatter={(v: number) => (v * 100).toFixed(0)}
              domain={yDomain}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatIV(value),
                name.replace('iv_', 'DTE: '),
              ]}
              labelFormatter={(label: number) => `Strike: ${label}`}
              contentStyle={{
                fontSize: '11px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine
              x={spot}
              stroke={theme.palette.info.main}
              strokeDasharray="5 5"
              label={{
                value: 'ATM',
                position: 'top',
                fontSize: 9,
                fill: theme.palette.info.main,
              }}
            />
            {expirationsEMA.map((exp, idx) => (
              <Line
                key={exp.dte}
                type="monotone"
                dataKey={`iv_${exp.dte}`}
                stroke={colors[idx % colors.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name={`DTE: ${exp.dte}`}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
      {skewData.forward_vol_data != null && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            Forward Vol Curve ({skewData.forward_vol_data.front_expiration} →{' '}
            {skewData.forward_vol_data.back_expiration}, DTE:{' '}
            {skewData.forward_vol_data.forward_dte})
          </Typography>
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={(() => {
                  const smoothedIVs = getEMA(
                    skewData.forward_vol_data.forward_ivs,
                  );
                  return skewData.forward_vol_data.strikes
                    .slice(1)
                    .map((strike, i) => ({
                      strike,
                      fwdIV: smoothedIVs[i + 1],
                    }));
                })()}
                margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="strike"
                  type="number"
                  tick={{ fontSize: 10 }}
                  domain={xDomain}
                  tickFormatter={(v: number) => v.toFixed(0)}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: number) => (v * 100).toFixed(0)}
                  domain={[
                    (dataMin: number) => dataMin - 0.02,
                    (dataMax: number) => Math.min(dataMax + 0.02, 4.0),
                  ]}
                />
                <Tooltip
                  formatter={(value: number) => [formatIV(value), 'Fwd IV']}
                  labelFormatter={(label: number) => `Strike: ${label}`}
                  contentStyle={{
                    fontSize: '11px',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
                <ReferenceLine
                  x={skewData.expirations[0]?.spot ?? 0}
                  stroke={theme.palette.info.main}
                  strokeDasharray="5 5"
                  label={{
                    value: 'ATM',
                    position: 'top',
                    fontSize: 9,
                    fill: theme.palette.info.main,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="fwdIV"
                  stroke={theme.palette.secondary.main}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const MoviefoneAdvisor = ({ data, onAsk }: MoviefoneAdvisorProps) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  console.log(
    '=== moviefone output ===',
    data,
    data.text_output,
    TOOL_DEFINITIONS,
  );

  const {
    regime,
    enriched_regime,
    volatility,
    forecast,
    mispriced_options,
    strategies,
    strategy_catalog,
    skew_plot_data,
    tvtp_regime,
    correlation_regime,
    arbitrage_enforcement,
    scenario_forecast,
    candidate_tables,
    svi_surface,
    projected_surface,
    surface_residuals,
    vrp_term_structure,
    spot_vol_regime,
    strategy_rules,
    ml_features,
  } = data;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const tabOptions = new Map<string, string>([
    ['overview', 'Overview'],
    ['strategies', 'Strategies'],
    ['opportunities', 'Opportunities'],
    ['volatility', 'Volatility'],
  ]);

  const volRowsLeft = [
    { label: 'Spot', value: usd(data.spot) },
    { label: 'Forward (30d)', value: usd(volatility.forward_30d) },
    { label: 'ATM IV (7d)', value: pct(volatility.atm_iv_7d) },
    { label: 'ATM IV (30d)', value: pct(volatility.atm_iv_30d) },
    { label: 'ATM IV (90d)', value: pct(volatility.atm_iv_90d) },
    {
      label: 'RV (c2c)',
      value: `${pct(volatility.realized_vol.estimate)}  [${volatility.realized_vol.method}]`,
    },
    {
      label: 'VRP',
      value: `${volatility.realized_vol.vrp > 0 ? '+' : ''}${pct(volatility.realized_vol.vrp)}  [${volatility.realized_vol.vrp_regime}]`,
    },
    { label: 'RV 5d', value: pct(volatility.realized_vol.rv_5d) },
    { label: 'RV 20d', value: pct(volatility.realized_vol.rv_20d) },
  ];

  const volRowsRight = [
    { label: 'Trend', value: volatility.realized_vol.trend },
    {
      label: 'Fwd Vol',
      value:
        volatility.forward_vol != null
          ? `${pct(volatility.forward_vol.forward_iv)}  ${volatility.forward_vol.term_slope}`
          : 'N/A',
    },
    {
      label: 'VT-adj',
      value:
        volatility.vt_forward_vol != null
          ? pct(volatility.vt_forward_vol)
          : 'N/A',
    },
    { label: '25d Skew', value: `+${pct(volatility.skew_25d_30d)}` },
    { label: 'n Days', value: `${volatility.realized_vol.n_days}` },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <TabContext value={activeTab}>
        {/* Tab Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0,
            padding: '0 16px',
          }}
        >
          <Tabs
            options={tabOptions}
            onChange={handleTabChange}
            defaultScrollable={true}
            fontSize="13px"
            tabButtonSx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: '13px',
              padding: '12px 20px',
              position: 'relative',
              '&:hover': { color: theme.palette.text.primary },
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '12px',
                  right: '12px',
                  height: '2px',
                  background: theme.palette.primary.main,
                  borderRadius: '1px',
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <TabPanel value="overview" sx={{ p: 0 }}>
            <Stack sx={{ gap: '14px' }}>
              {/* Row 1: Regime + Vol Snapshot */}
              <Stack
                direction="row"
                sx={{ gap: '14px', alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Section
                    title={`Regime: ${regime.regime} ${pct0(regime.confidence)}`}
                  >
                    <KVTable
                      section="Regime"
                      onAsk={onAsk}
                      rows={[
                        { label: 'Overlay', value: enriched_regime.overlay },
                        { label: 'IV Level', value: regime.iv_level },
                        { label: 'Term Struct', value: regime.term_structure },
                        {
                          label: 'IV Rank',
                          value:
                            enriched_regime.iv_rank != null
                              ? `${num(enriched_regime.iv_rank, 0)}th pctl`
                              : 'N/A',
                        },
                        {
                          label: 'Surf Quality',
                          value: num(volatility.surface_quality.composite),
                        },
                        {
                          label: 'Blend',
                          value: `${pct0(volatility.blend_weight)} pri / ${pct0(1 - volatility.blend_weight)} alt`,
                        },
                        {
                          label: 'Event Flag',
                          value: regime.event_flag ? 'YES' : 'No',
                        },
                        {
                          label: 'Spot-IV Corr',
                          value: num(regime.spot_atm_corr, 2),
                        },
                        {
                          label: 'Skew-IV Corr',
                          value: num(regime.spot_skew_corr, 2),
                        },
                        ...(spot_vol_regime != null && spot_vol_regime !== ''
                          ? [
                              {
                                label: 'Spot Vol Reg',
                                value: spot_vol_regime
                                  .replace(/([A-Z])/g, ' $1')
                                  .trim(),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Section>
                </Box>

                <Box sx={{ minWidth: 0, flex: 2 }}>
                  <Section title="Volatility Snapshot">
                    <KVTable
                      section="Volatility Snapshot"
                      onAsk={onAsk}
                      rows={[...volRowsLeft, ...volRowsRight]}
                      valueColorFn={(label, value) => {
                        if (label === 'VRP') {
                          const vrpMatch = value.match(/([+-]?[\d.]+)%/);
                          if (vrpMatch != null) {
                            return getVRPColor(
                              parseFloat(vrpMatch[1]) / 100,
                              theme,
                            );
                          }
                        }
                        return undefined;
                      }}
                    />
                  </Section>
                </Box>
              </Stack>

              {/* Row 2: Correlation Regime + Forecast */}
              <Stack
                direction="row"
                sx={{ gap: '14px', alignItems: 'flex-start' }}
              >
                {/* Correlation Regime - only show when metrics exist (non-UNKNOWN) */}
                {correlation_regime != null &&
                  correlation_regime.metrics.sector_etf !== 'UNKNOWN' && (
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Section
                        title={`Correlation Regime: ${correlation_regime.regime}`}
                      >
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="Correlation Regime"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'Regime',
                                value: correlation_regime.regime
                                  .replace(/([A-Z])/g, ' $1')
                                  .trim(),
                              },
                              {
                                label: 'Confidence',
                                value: pct0(correlation_regime.confidence),
                              },
                              {
                                label: 'Vol Multiplier',
                                value: `${num(correlation_regime.vol_multiplier, 2)}x`,
                              },
                              {
                                label: 'MR Speed',
                                value: `${num(correlation_regime.mr_speed_multiplier, 2)}x`,
                              },
                              {
                                label: 'Reason',
                                value:
                                  correlation_regime.detection_reason.split(
                                    ':',
                                  )[0],
                              },
                            ]}
                          />
                          <Box sx={{ px: 0.75, py: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                display: 'block',
                                mb: 0.5,
                              }}
                            >
                              Probabilities
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ mb: 0.5 }}
                            >
                              {[
                                {
                                  label: 'IDIO',
                                  value: correlation_regime.prob_idiosyncratic,
                                },
                                {
                                  label: 'SECTOR',
                                  value: correlation_regime.prob_sector_stress,
                                },
                                {
                                  label: 'SYS',
                                  value: correlation_regime.prob_systemic,
                                },
                              ].map((p) => (
                                <Box
                                  key={p.label}
                                  sx={{ flex: 1, textAlign: 'center' }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.text.primary,
                                      display: 'block',
                                    }}
                                  >
                                    {p.label}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {pct0(p.value)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                          <MiniTable<MoviefoneCorrelationMetrics>
                            section="Correlation Metrics"
                            onAsk={onAsk}
                            headers={['Metric', 'Value']}
                            rawData={[correlation_regime.metrics]}
                            rows={[
                              [
                                'Corr SPY 20d',
                                num(correlation_regime.metrics.corr_spy_20d, 2),
                              ],
                              [
                                'Corr SPY 60d',
                                num(correlation_regime.metrics.corr_spy_60d, 2),
                              ],
                              [
                                'Corr Sector 20d',
                                num(
                                  correlation_regime.metrics.corr_sector_20d,
                                  2,
                                ),
                              ],
                              [
                                'Corr Sector 60d',
                                num(
                                  correlation_regime.metrics.corr_sector_60d,
                                  2,
                                ),
                              ],
                              [
                                'Implied Corr',
                                num(
                                  correlation_regime.metrics
                                    .implied_correlation,
                                  2,
                                ),
                              ],
                              [
                                'Sector ETF',
                                correlation_regime.metrics.sector_etf,
                              ],
                            ]}
                          />
                        </Stack>
                      </Section>
                    </Box>
                  )}

                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: '600px',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                      borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.action.disabled,
                      borderRadius: '6px',
                      border: `3px solid transparent`,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Section title="Forecast (1d / 5d)">
                    <KVTable
                      section="Forecast"
                      onAsk={onAsk}
                      rows={[
                        {
                          label: 'ATM IV 30d',
                          value: pct(forecast.current_atm),
                        },
                        {
                          label: 'Forecast 1d',
                          value: pct(forecast.forecast_1d),
                        },
                        {
                          label: 'Forecast 5d',
                          value: pct(forecast.forecast_5d),
                        },
                        {
                          label: 'Range',
                          value: `${pct(forecast.forecast_low)} - ${pct(forecast.forecast_high)}`,
                        },
                        {
                          label: 'Direction',
                          value: `${forecast.direction} (${pct0(forecast.confidence)})`,
                        },
                        { label: 'Model', value: forecast.model },
                        {
                          label: 'MR Speed',
                          value: num(enriched_regime.adjusted_mr_speed, 3),
                        },
                        {
                          label: 'MR Target',
                          value: pct(forecast.mr_target),
                        },
                      ]}
                    />
                  </Section>

                  {data.vol_forecast != null &&
                    (data.vol_forecast.har_forecast_5d != null ||
                      data.vol_forecast.garch_forecast_10d != null ||
                      data.vol_forecast.ensemble_forecast != null) && (
                      <Section title="Vol Forecast">
                        <KVTable
                          section="Vol Forecast"
                          onAsk={onAsk}
                          rows={[
                            ...(data.vol_forecast.har_forecast_5d != null
                              ? [
                                  {
                                    label: 'HAR 5d',
                                    value: pct(
                                      data.vol_forecast.har_forecast_5d,
                                    ),
                                  },
                                ]
                              : []),
                            ...(data.vol_forecast.garch_forecast_10d != null
                              ? [
                                  {
                                    label: 'GARCH 10d',
                                    value: pct(
                                      data.vol_forecast.garch_forecast_10d,
                                    ),
                                  },
                                ]
                              : []),
                            ...(data.vol_forecast.ensemble_forecast != null
                              ? [
                                  {
                                    label: 'Ensemble',
                                    value: pct(
                                      data.vol_forecast.ensemble_forecast
                                        .point_estimate,
                                    ),
                                  },
                                  {
                                    label: '95% CI',
                                    value: `${pct(data.vol_forecast.ensemble_forecast.lower_bound)} - ${pct(data.vol_forecast.ensemble_forecast.upper_bound)}`,
                                  },
                                ]
                              : []),
                            ...(data.vol_forecast.har_r_squared != null
                              ? [
                                  {
                                    label: 'HAR R²',
                                    value: num(
                                      data.vol_forecast.har_r_squared,
                                      2,
                                    ),
                                  },
                                ]
                              : []),
                            ...(data.vol_forecast.garch_persistence != null
                              ? [
                                  {
                                    label: 'GARCH Persist',
                                    value: num(
                                      data.vol_forecast.garch_persistence,
                                      3,
                                    ),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </Section>
                    )}
                </Box>
              </Stack>
              {data.synth_oi != null && (
                <Section title="Synthetic OI">
                  <KVTable
                    section="Synthetic OI"
                    onAsk={onAsk}
                    rows={[
                      {
                        label: 'Direction',
                        value: data.synth_oi.entity_direction.toUpperCase(),
                      },
                      ...(data.synth_oi.iv_rank != null
                        ? [
                            {
                              label: 'IV Rank',
                              value: `${Math.round(data.synth_oi.iv_rank * 100)}th`,
                            },
                          ]
                        : []),
                      ...(data.synth_oi.skew_rank != null
                        ? [
                            {
                              label: 'Skew Rank',
                              value: `${Math.round(data.synth_oi.skew_rank * 100)}th`,
                            },
                          ]
                        : []),
                      ...(data.synth_oi.iv_pct != null
                        ? [
                            {
                              label: 'IV Pct',
                              value: pct(data.synth_oi.iv_pct),
                            },
                          ]
                        : []),
                      {
                        label: 'Spot',
                        value: usd(data.synth_oi.spot),
                      },
                    ]}
                  />
                  {data.synth_oi.key_levels.slice(0, 4).map((lv, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: theme.palette.text.primary,
                        fontFamily: 'monospace',
                      }}
                    >
                      {lv.level_type === 'resistance' ? '▲' : '▼'} $
                      {lv.price.toFixed(2)} {lv.entity} [{lv.strength}]
                    </Typography>
                  ))}
                </Section>
              )}

              {/* Charts (moved from charts tab) */}
              <Stack
                direction="row"
                sx={{ gap: '14px', alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {skew_plot_data != null && (
                    <Section title="IV Skew vs Strike">
                      <SkewPlotSection skewData={skew_plot_data} />
                    </Section>
                  )}
                  {data.dollar_value_summaries?.length > 0 && (
                    <Section title="Dollar Value Summary">
                      <MiniTable<DollarValueSummary>
                        section="Dollar Value Summary"
                        onAsk={onAsk}
                        headers={[
                          'Expiration',
                          'DTE',
                          'Contracts',
                          'Total DV',
                          'Avg DV',
                        ]}
                        rows={data.dollar_value_summaries.map(
                          (s: DollarValueSummary) => [
                            s.expiration,
                            `${s.dte}d`,
                            `${s.contract_count}`,
                            usd(s.total_dollar_value),
                            usd(s.avg_dollar_value),
                          ],
                        )}
                        columnColorFns={[
                          () => undefined,
                          () => undefined,
                          () => undefined,
                          (_v, _r, _i, raw) =>
                            raw != null && raw.total_dollar_value > 0
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          (_v, _r, _i, raw) =>
                            raw != null && raw.avg_dollar_value > 0
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                        ]}
                      />
                    </Section>
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {data.vol_surface_3d != null &&
                    data.vol_surface_3d.expirations.length > 0 && (
                      <Section title="Volatility Surface (3D)">
                        <VolatilityChartPanel
                          surfaceData={data.vol_surface_3d}
                        />
                      </Section>
                    )}
                  {data.dollar_value_contracts?.length > 0 && (
                    <Section title="Dollar Value Per Contract">
                      <DollarValueChart3D
                        contracts={data.dollar_value_contracts}
                      />
                    </Section>
                  )}
                </Box>
              </Stack>
            </Stack>
          </TabPanel>

          <TabPanel value="strategies" sx={{ p: 0 }}>
            <Stack sx={{ gap: '14px' }}>
              <Stack
                direction="row"
                sx={{ gap: '14px', alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 2 }}>
                  <Section title="Strategies">
                    {strategies.length === 0 ? (
                      <Typography
                        variant="body2"
                        sx={{
                          px: 0.75,
                          py: 0.5,
                          color: theme.palette.text.primary,
                          fontStyle: 'italic',
                        }}
                      >
                        No specific strategies recommended — regime is uncertain
                      </Typography>
                    ) : (
                      <Stack sx={{ gap: '14px' }}>
                        {strategies.map((s: MoviefoneStrategy, i: number) => (
                          <MoviefoneStrategyCard
                            key={i}
                            searchCandidate={s as unknown as SearchCandidate}
                            data={data as unknown as MoviefoneAnalysis}
                            onAsk={onAsk}
                          />
                        ))}
                      </Stack>
                    )}
                  </Section>
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: '600px',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                      borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.action.disabled,
                      borderRadius: '6px',
                      border: `3px solid transparent`,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Section title="Strategy Catalog Scores">
                    <Stack sx={{ gap: '14px' }}>
                      {strategy_catalog.map(
                        (c: MoviefoneCatalogEntry, i: number) => (
                          <Box
                            key={i}
                            sx={{
                              py: 0.5,
                              cursor: onAsk != null ? 'pointer' : 'default',
                              borderBottom:
                                i < strategy_catalog.length - 1
                                  ? `1px solid ${theme.palette.divider}`
                                  : 'none',
                              mx: '2px',
                            }}
                            onClick={() =>
                              onAsk?.(
                                `Explain the ${c.name} Strategy. What is it and when is it used? Score: ${num(c.score)}, Category: ${c.category}. Description: ${c.description}`,
                              )
                            }
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ mb: 0.25 }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {c.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.primary }}
                              >
                                ({c.category})
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: getScoreColor(c.score, theme),
                                  fontWeight: 700,
                                  ml: 'auto',
                                }}
                              >
                                {num(c.score)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                display: 'block',
                                lineHeight: 1.4,
                              }}
                            >
                              {c.description}
                            </Typography>
                          </Box>
                        ),
                      )}
                    </Stack>
                  </Section>
                </Box>
              </Stack>
            </Stack>
          </TabPanel>

          <TabPanel value="opportunities" sx={{ p: 0 }}>
            <Stack sx={{ gap: '14px' }}>
              <Stack
                direction="row"
                sx={{ gap: '14px', alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Section
                    title={`Mispriced Options (scanned: ${mispriced_options.total_scanned})`}
                  >
                    <Stack sx={{ gap: '14px' }}>
                      <Box sx={{ px: 0.75, py: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', display: 'block' }}
                        >
                          Signal Bias: {mispriced_options.bias} | R²:{' '}
                          {num(mispriced_options.r_squared, 3)} | Avg Error:{' '}
                          {pct(mispriced_options.avg_model_error)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                        >
                          RICH (sell candidates):
                        </Typography>
                        {mispriced_options.rich.length > 0 ? (
                          <MiniTable
                            section="Mispriced Options (Rich)"
                            onAsk={onAsk}
                            headers={[
                              'Strike',
                              'Expiry',
                              'DTE',
                              'Mkt IV',
                              'Fair IV',
                              'Z',
                              'Delta',
                            ]}
                            rawData={mispriced_options.rich}
                            rows={mispriced_options.rich.map(
                              (o: MoviefoneMispricedOption) => [
                                `${data.symbol} ${o.strike}${o.is_put ? 'P' : 'C'}`,
                                o.expiry,
                                `${o.dte}d`,
                                pct(o.market_iv),
                                pct(o.fair_iv),
                                `+${num(o.zscore, 1)}`,
                                num(o.delta),
                              ],
                            )}
                            columnColorFns={[
                              () => undefined,
                              () => undefined,
                              () => undefined,
                              () => undefined,
                              () => undefined,
                              (_v, _r, _i, raw) =>
                                raw != null
                                  ? getZScoreColor(raw.zscore, theme)
                                  : undefined,
                              (_v, _r, _i, raw) =>
                                raw != null
                                  ? getGreeksColor(raw.delta, theme)
                                  : undefined,
                            ]}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            None found
                          </Typography>
                        )}
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                        >
                          CHEAP (buy candidates):
                        </Typography>
                        {mispriced_options.cheap.length > 0 ? (
                          <MiniTable
                            section="Mispriced Options (Cheap)"
                            onAsk={onAsk}
                            headers={[
                              'Strike',
                              'Expiry',
                              'DTE',
                              'Mkt IV',
                              'Fair IV',
                              'Z',
                              'Delta',
                            ]}
                            rawData={mispriced_options.cheap}
                            rows={mispriced_options.cheap.map(
                              (o: MoviefoneMispricedOption) => [
                                `${data.symbol} ${o.strike}${o.is_put ? 'P' : 'C'}`,
                                o.expiry,
                                `${o.dte}d`,
                                pct(o.market_iv),
                                pct(o.fair_iv),
                                num(o.zscore, 1),
                                num(o.delta),
                              ],
                            )}
                            columnColorFns={[
                              () => undefined,
                              () => undefined,
                              () => undefined,
                              () => undefined,
                              () => undefined,
                              (_v, _r, _i, raw) =>
                                raw != null
                                  ? getZScoreColor(raw.zscore, theme)
                                  : undefined,
                              (_v, _r, _i, raw) =>
                                raw != null
                                  ? getGreeksColor(raw.delta, theme)
                                  : undefined,
                            ]}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            None found
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Section>
                </Box>
              </Stack>

              {candidate_tables.length > 0 && (
                <Stack
                  direction="row"
                  sx={{ gap: '14px', alignItems: 'flex-start' }}
                >
                  {candidate_tables.map(
                    (ct: MoviefoneCandidateTable, ci: number) => (
                      <Box key={ci} sx={{ minWidth: 0, flex: 1 }}>
                        <Section title={ct.strategy_name}>
                          <Box sx={{ px: 0.75, py: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                display: 'block',
                                color: theme.palette.text.primary,
                              }}
                            >
                              ATM IV: {pct(ct.chain_atm_iv)} | Median IV:{' '}
                              {pct(ct.chain_median_iv)}
                            </Typography>
                          </Box>
                          {ct.expirations.map((exp, ei) => (
                            <Box key={ei}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  display: 'block',
                                  px: 0.75,
                                  py: 0.25,
                                }}
                              >
                                {exp.date} ({exp.dte}d) ATM: {pct(exp.atm_iv)}
                              </Typography>
                              {exp.candidates.length > 0 && (
                                <MiniTable
                                  section={`${ct.strategy_name} Candidates`}
                                  onAsk={onAsk}
                                  headers={[
                                    '#',
                                    'Strike',
                                    'Type',
                                    'Delta',
                                    'IV',
                                    'Theta',
                                    'Vega',
                                    'Score',
                                  ]}
                                  rows={exp.candidates.map((c) => [
                                    `${c.rank}`,
                                    `${c.strike}`,
                                    c.option_type,
                                    num(c.delta),
                                    pct(c.iv),
                                    num(c.theta, 4),
                                    num(c.vega, 4),
                                    num(c.score, 1),
                                  ])}
                                />
                              )}
                            </Box>
                          ))}
                        </Section>
                      </Box>
                    ),
                  )}
                </Stack>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value="volatility" sx={{ p: 0 }}>
            <Stack sx={{ gap: '14px' }}>
              <Stack
                direction="row"
                sx={{ gap: '14px', alignItems: 'flex-start' }}
              >
                {/* Term Structure */}
                {volatility.term_structure != null && (
                  <Box sx={{ minWidth: 0, flex: 2 }}>
                    <Section
                      title={`Term Structure${volatility.term_structure.event_detected ? ' [EVENT]' : ''}`}
                    >
                      <Stack sx={{ gap: '14px' }}>
                        {volatility.term_structure.forward_vols.length > 0 && (
                          <MiniTable<MoviefoneForwardVolPair>
                            section="Term Structure"
                            onAsk={onAsk}
                            headers={[
                              'Short',
                              'Long',
                              'Dirty Fwd',
                              'Clean Fwd',
                              'Valid',
                            ]}
                            rawData={volatility.term_structure.forward_vols}
                            rows={volatility.term_structure.forward_vols.map(
                              (fv: MoviefoneForwardVolPair) => [
                                `${fv.short_dte}d`,
                                `${fv.long_dte}d`,
                                pct(fv.forward_vol_dirty),
                                pct(fv.forward_vol_clean),
                                fv.is_valid ? 'Yes' : 'No',
                              ],
                            )}
                          />
                        )}
                        {volatility.term_structure.garch_params != null && (
                          <KVTable
                            section="Term Structure GARCH"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'GARCH sigma_i',
                                value: pct(
                                  volatility.term_structure.garch_params
                                    .sigma_i,
                                ),
                              },
                              {
                                label: 'GARCH sigma_f',
                                value: pct(
                                  volatility.term_structure.garch_params
                                    .sigma_final,
                                ),
                              },
                              {
                                label: 'Half-life',
                                value: `${num(volatility.term_structure.garch_params.half_life, 0)}d`,
                              },
                              {
                                label: 'Max Fwd Ratio',
                                value: num(
                                  volatility.term_structure
                                    .max_forward_vol_ratio,
                                ),
                              },
                            ]}
                          />
                        )}
                        {volatility.all_forward_vols.length > 0 && (
                          <MiniTable
                            section="All Forward Vols"
                            onAsk={onAsk}
                            headers={['Short DTE', 'Long DTE', 'Forward Vol']}
                            rows={volatility.all_forward_vols.map((fv) => [
                              `${fv[0]}d`,
                              `${fv[1]}d`,
                              pct(fv[2]),
                            ])}
                          />
                        )}
                        {volatility.rv_estimators != null && (
                          <MiniTable
                            section="RV Estimators"
                            onAsk={onAsk}
                            headers={['Method', 'RV']}
                            rows={[
                              [
                                'Parkinson',
                                volatility.rv_estimators.parkinson != null
                                  ? pct(volatility.rv_estimators.parkinson)
                                  : 'N/A',
                              ],
                              [
                                'Garman-Klass',
                                volatility.rv_estimators.garman_klass != null
                                  ? pct(volatility.rv_estimators.garman_klass)
                                  : 'N/A',
                              ],
                              [
                                'Yang-Zhang',
                                volatility.rv_estimators.yang_zhang != null
                                  ? pct(volatility.rv_estimators.yang_zhang)
                                  : 'N/A',
                              ],
                              [
                                'Rogers-Satchell',
                                volatility.rv_estimators.rogers_satchell != null
                                  ? pct(
                                      volatility.rv_estimators.rogers_satchell,
                                    )
                                  : 'N/A',
                              ],
                              [
                                'Best Estimate',
                                pct(volatility.rv_estimators.best_estimate),
                              ],
                              [
                                'Best Method',
                                volatility.rv_estimators.best_method,
                              ],
                            ]}
                          />
                        )}
                      </Stack>
                    </Section>
                  </Box>
                )}

                {/* Event Signal + Carry Quality column */}
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: '600px',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                      borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.action.disabled,
                      borderRadius: '6px',
                      border: `3px solid transparent`,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Stack sx={{ gap: '14px' }}>
                    {/* Event Signal */}
                    {volatility.event_signal != null && (
                      <Section
                        title={`Event Signal${volatility.event_signal.detected ? ' [DETECTED]' : ''}`}
                      >
                        <KVTable
                          section="Event Signal"
                          onAsk={onAsk}
                          rows={[
                            {
                              label: 'Detected',
                              value: volatility.event_signal.detected
                                ? 'YES'
                                : 'No',
                            },
                            ...(volatility.event_signal.detected
                              ? [
                                  {
                                    label: 'Source',
                                    value: volatility.event_signal.event_source,
                                  },
                                  {
                                    label: 'Confidence',
                                    value: pct0(
                                      volatility.event_signal.confidence,
                                    ),
                                  },
                                  ...(volatility.event_signal.days_to_event !==
                                  null
                                    ? [
                                        {
                                          label: 'Days to Event',
                                          value: `${volatility.event_signal.days_to_event}`,
                                        },
                                      ]
                                    : []),
                                  {
                                    label: 'Fwd Vol Ratio',
                                    value: num(
                                      volatility.event_signal.forward_vol_ratio,
                                    ),
                                  },
                                  ...(volatility.event_signal.is_post_event
                                    ? [
                                        {
                                          label: 'Post-Event',
                                          value: 'YES',
                                        },
                                      ]
                                    : []),
                                ]
                              : []),
                          ]}
                          valueColorFn={(label) => {
                            if (
                              label === 'Detected' &&
                              volatility.event_signal?.detected === true
                            ) {
                              return theme.palette.warning.main;
                            }
                            return undefined;
                          }}
                        />
                      </Section>
                    )}

                    {/* Carry Quality */}
                    {volatility.carry_quality != null && (
                      <Section title="Carry Quality">
                        <KVTable
                          section="Carry Quality"
                          onAsk={onAsk}
                          rows={[
                            {
                              label: 'Bergomi Sharpe',
                              value: num(volatility.carry_quality.carry_sharpe),
                            },
                            {
                              label: 'Conviction',
                              value: volatility.carry_quality.quality,
                            },
                          ]}
                          valueColorFn={(label, value) => {
                            if (label === 'Conviction') {
                              return getCarryColor(value, theme);
                            }
                            return undefined;
                          }}
                        />
                      </Section>
                    )}

                    {/* Arbitrage Enforcement */}
                    {arbitrage_enforcement != null && (
                      <Section title="Arbitrage Enforcement">
                        <KVTable
                          section="Arbitrage Enforcement"
                          onAsk={onAsk}
                          rows={[
                            {
                              label: 'Calendar Fixes',
                              value: `${arbitrage_enforcement.calendar_adjustments}`,
                            },
                            {
                              label: 'Butterfly Fixes',
                              value: `${arbitrage_enforcement.butterfly_adjustments}`,
                            },
                            {
                              label: 'BF Violations',
                              value: `${arbitrage_enforcement.butterfly_violations}`,
                            },
                          ]}
                          valueColorFn={(label, value) => {
                            const n = parseInt(value, 10);
                            if (n > 0 && label !== 'BF Violations') {
                              return theme.palette.warning.main;
                            }
                            if (n > 0 && label === 'BF Violations') {
                              return theme.palette.error.light;
                            }
                            return theme.palette.success.main;
                          }}
                        />
                      </Section>
                    )}

                    {/* Correlation Regime moved to Overview tab */}

                    {/* TVTP Regime */}
                    {tvtp_regime != null && (
                      <Section title="TVTP Regime">
                        <TVTPRegimeBars tvtp={tvtp_regime} />
                      </Section>
                    )}
                  </Stack>
                </Box>
              </Stack>

              {/* Scenario Forecast */}
              {scenario_forecast != null && (
                <Section title="Scenario Forecast">
                  {scenario_forecast.scenarios.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        px: 0.75,
                        py: 0.5,
                        color: theme.palette.text.primary,
                        fontStyle: 'italic',
                      }}
                    >
                      No scenario analysis available for this symbol yet
                    </Typography>
                  ) : (
                    <Stack sx={{ gap: '14px' }}>
                      <Box sx={{ px: 0.75, py: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', display: 'block' }}
                        >
                          IV Range: {pct(scenario_forecast.iv_range_low)} -{' '}
                          {pct(scenario_forecast.iv_range_high)} | Bias:{' '}
                          {scenario_forecast.directional_bias}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontStyle: 'italic',
                            color: theme.palette.text.primary,
                          }}
                        >
                          {scenario_forecast.key_insight}
                        </Typography>
                      </Box>
                      <MiniTable
                        section="Scenario Forecast"
                        onAsk={onAsk}
                        headers={[
                          'Scenario',
                          'Spot Move',
                          'Final IV',
                          'Regime',
                          'Notes',
                        ]}
                        rows={scenario_forecast.scenarios.map((sc) => [
                          sc.name,
                          `${sc.spot_change > 0 ? '+' : ''}${pct(sc.spot_change)}`,
                          pct(sc.final_atm_iv),
                          sc.regime_likely,
                          sc.notes,
                        ])}
                      />
                    </Stack>
                  )}
                </Section>
              )}

              {/* Scenario Pricing */}
              {data.scenario_pricing != null && (
                <Section
                  title={`Scenario Pricing (Spot=$${data.scenario_pricing.scenario_spot.toFixed(2)}, DaysAhead=${data.scenario_pricing.scenario_days})`}
                >
                  <MiniTable
                    section="Scenario Pricing"
                    onAsk={onAsk}
                    headers={[
                      'DTE',
                      'Strike',
                      'MktCall',
                      'ScenCall',
                      'CallDiff',
                      'ScenIV',
                      'LogM',
                    ]}
                    rows={data.scenario_pricing.top_contracts.map((c) => [
                      `${c.dte}`,
                      c.strike.toFixed(1),
                      c.market_call.toFixed(2),
                      c.scenario_call.toFixed(2),
                      (c.call_diff >= 0 ? '+' : '') + c.call_diff.toFixed(2),
                      `${(c.scenario_iv * 100).toFixed(1)}%`,
                      c.scenario_moneyness.toFixed(4),
                    ])}
                    columnColorFns={[
                      () => undefined,
                      () => undefined,
                      () => undefined,
                      () => undefined,
                      (v: string) =>
                        v.startsWith('+')
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      () => undefined,
                      () => undefined,
                    ]}
                  />
                  <Box sx={{ px: 0.75, py: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Total contracts: {data.scenario_pricing.total_contracts} |
                      Showing top {data.scenario_pricing.top_contracts.length}{' '}
                      by |call_diff|
                    </Typography>
                  </Box>
                </Section>
              )}

              {/* Candidate Tables */}
              {candidate_tables.length > 0 && (
                <Stack
                  direction="row"
                  sx={{ gap: '14px', alignItems: 'flex-start' }}
                >
                  {candidate_tables.map(
                    (ct: MoviefoneCandidateTable, ci: number) => (
                      <Box key={ci} sx={{ minWidth: 0, flex: 1 }}>
                        <Section title={ct.strategy_name}>
                          <Box sx={{ px: 0.75, py: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                display: 'block',
                                color: theme.palette.text.primary,
                              }}
                            >
                              ATM IV: {pct(ct.chain_atm_iv)} | Median IV:{' '}
                              {pct(ct.chain_median_iv)}
                            </Typography>
                          </Box>
                          {ct.expirations.map((exp, ei) => (
                            <Box key={ei}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  display: 'block',
                                  px: 0.75,
                                  py: 0.25,
                                }}
                              >
                                {exp.date} ({exp.dte}d) ATM: {pct(exp.atm_iv)}
                              </Typography>
                              {exp.candidates.length > 0 && (
                                <MiniTable
                                  section={`${ct.strategy_name} Candidates`}
                                  onAsk={onAsk}
                                  headers={[
                                    '#',
                                    'Strike',
                                    'Type',
                                    'Delta',
                                    'IV',
                                    'Theta',
                                    'Vega',
                                    'Score',
                                  ]}
                                  rows={exp.candidates.map((c) => [
                                    `${c.rank}`,
                                    `${c.strike}`,
                                    c.option_type,
                                    num(c.delta),
                                    pct(c.iv),
                                    num(c.theta, 4),
                                    num(c.vega, 4),
                                    num(c.score, 1),
                                  ])}
                                />
                              )}
                            </Box>
                          ))}
                        </Section>
                      </Box>
                    ),
                  )}
                </Stack>
              )}

              {/* SVI Surface + VRP Term Structure row */}
              {(svi_surface != null || vrp_term_structure != null) && (
                <Stack
                  direction="row"
                  sx={{ gap: '14px', alignItems: 'flex-start' }}
                >
                  {svi_surface != null && (
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Section
                        title={`SVI Surface (${svi_surface.n_slices} slices)`}
                      >
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="SVI Surface"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'Arb Composite',
                                value: num(
                                  svi_surface.arbitrage_quality.composite,
                                ),
                              },
                              {
                                label: 'Butterfly Clean',
                                value: svi_surface.arbitrage_quality
                                  .butterfly_clean
                                  ? 'Yes'
                                  : 'No',
                              },
                              {
                                label: 'Calendar Clean',
                                value: svi_surface.arbitrage_quality
                                  .calendar_clean
                                  ? 'Yes'
                                  : 'No',
                              },
                              ...(svi_surface.fit_quality != null
                                ? [
                                    {
                                      label: 'Fit RMSE',
                                      value: num(
                                        svi_surface.fit_quality.overall_rmse,
                                        4,
                                      ),
                                    },
                                    {
                                      label: 'Max Slice RMSE',
                                      value: `${num(svi_surface.fit_quality.max_slice_rmse, 4)} (${svi_surface.fit_quality.worst_slice_dte}d)`,
                                    },
                                  ]
                                : []),
                            ]}
                            valueColorFn={(label, value) => {
                              if (
                                label === 'Butterfly Clean' ||
                                label === 'Calendar Clean'
                              ) {
                                return value === 'Yes'
                                  ? theme.palette.success.main
                                  : theme.palette.error.main;
                              }
                              return undefined;
                            }}
                          />
                        </Stack>
                      </Section>
                    </Box>
                  )}

                  {vrp_term_structure != null && (
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: '600px',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                          borderRadius: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: theme.palette.action.disabled,
                          borderRadius: '6px',
                          border: `3px solid transparent`,
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Section title="VRP Term Structure">
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="VRP Term Structure"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'Richest Tenor',
                                value: `${vrp_term_structure.richest_dte}d`,
                              },
                              {
                                label: 'Cheapest Tenor',
                                value: `${vrp_term_structure.cheapest_dte}d`,
                              },
                            ]}
                            valueColorFn={(label) => {
                              if (label === 'Richest Tenor') {
                                return theme.palette.error.main;
                              }
                              if (label === 'Cheapest Tenor') {
                                return theme.palette.success.main;
                              }
                              return undefined;
                            }}
                          />
                          {vrp_term_structure.tenors.length > 0 && (
                            <MiniTable
                              section="VRP Term Structure"
                              onAsk={onAsk}
                              headers={['DTE', 'IV', 'RV', 'Ratio', 'VRP']}
                              rows={vrp_term_structure.tenors.map((t) => [
                                `${t.dte}d`,
                                pct(t.iv),
                                pct(t.rv),
                                num(t.ratio),
                                `${t.level > 0 ? '+' : ''}${pct(t.level)}`,
                              ])}
                              columnColorFns={[
                                () => undefined,
                                () => undefined,
                                () => undefined,
                                (_v, _r, _i) => {
                                  const t = vrp_term_structure.tenors[_i];
                                  return getVRPColor(t.ratio - 1, theme);
                                },
                                (_v, _r, _i) => {
                                  const t = vrp_term_structure.tenors[_i];
                                  return getVRPColor(t.level, theme);
                                },
                              ]}
                            />
                          )}
                        </Stack>
                      </Section>
                    </Box>
                  )}
                </Stack>
              )}

              {/* Projected Surface + Surface Residuals row */}
              {(projected_surface != null || surface_residuals != null) && (
                <Stack
                  direction="row"
                  sx={{ gap: '14px', alignItems: 'flex-start' }}
                >
                  {projected_surface != null && (
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Section
                        title={`Projected Surface (${projected_surface.method})`}
                      >
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="Projected Surface"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'Projected ATM IV',
                                value: pct(projected_surface.projected_atm_iv),
                              },
                            ]}
                          />
                          {projected_surface.projected_expirations.length >
                            0 && (
                            <MiniTable
                              section="Projected Surface"
                              onAsk={onAsk}
                              headers={['DTE', 'ATM IV', '25d Skew']}
                              rows={projected_surface.projected_expirations.map(
                                (e) => [
                                  `${e.dte}d`,
                                  pct(e.atm_iv),
                                  `+${pct(e.skew_25d)}`,
                                ],
                              )}
                            />
                          )}
                        </Stack>
                      </Section>
                    </Box>
                  )}

                  {surface_residuals != null && (
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: '600px',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                          borderRadius: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: theme.palette.action.disabled,
                          borderRadius: '6px',
                          border: `3px solid transparent`,
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Section title="Surface Residuals">
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="Surface Residuals"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'RMSE',
                                value: num(surface_residuals.rmse, 4),
                              },
                              {
                                label: 'Wing Skew Richness',
                                value: num(
                                  surface_residuals.wing_skew_richness,
                                  3,
                                ),
                              },
                            ]}
                            valueColorFn={(label) => {
                              if (label === 'Wing Skew Richness') {
                                return getVRPColor(
                                  surface_residuals?.wing_skew_richness ?? 0,
                                  theme,
                                );
                              }
                              return undefined;
                            }}
                          />
                          {surface_residuals.points.length > 0 && (
                            <MiniTable
                              section="Surface Residuals"
                              onAsk={onAsk}
                              headers={['Label', 'DTE', 'Residual', 'Signal']}
                              rows={surface_residuals.points.map((p) => [
                                p.label,
                                `${p.dte}d`,
                                num(p.residual, 4),
                                p.signal,
                              ])}
                              columnColorFns={[
                                () => undefined,
                                () => undefined,
                                (_v, _r, _i) => {
                                  const p = surface_residuals.points[_i];
                                  return getVRPColor(p.residual, theme);
                                },
                                (v) => {
                                  if (v === 'Rich') {
                                    return theme.palette.error.main;
                                  }
                                  if (v === 'Cheap') {
                                    return theme.palette.success.main;
                                  }
                                  return theme.palette.text.primary;
                                },
                              ]}
                            />
                          )}
                        </Stack>
                      </Section>
                    </Box>
                  )}
                </Stack>
              )}

              {/* ML Features */}
              {ml_features != null && (
                <Section title="ML Feature Snapshot">
                  <Stack sx={{ gap: '14px' }}>
                    <Box sx={{ px: 0.75, py: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                      >
                        Trend Strength:{' '}
                        {num(ml_features.trend_strength.strength, 2)} |
                        Consecutive Moves:{' '}
                        {ml_features.trend_strength.consecutive_moves} |
                        Direction: {ml_features.trend_strength.direction}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {[
                          {
                            label: 'ST Ret',
                            value: ml_features.returns.short_term_return,
                            above: ml_features.returns.short_term_above_sma,
                            z: ml_features.z_scores.short_term_zscore,
                          },
                          {
                            label: 'MT Ret',
                            value: ml_features.returns.medium_term_return,
                            above: ml_features.returns.medium_term_above_sma,
                            z: ml_features.z_scores.medium_term_zscore,
                          },
                          {
                            label: 'LT Ret',
                            value: ml_features.returns.long_term_return,
                            above: ml_features.returns.long_term_above_sma,
                            z: ml_features.z_scores.long_term_zscore,
                          },
                        ].map((r) => (
                          <Box
                            key={r.label}
                            sx={{ flex: 1, textAlign: 'center', px: 0.5 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.primary,
                                display: 'block',
                              }}
                            >
                              {r.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                display: 'block',
                              }}
                            >
                              {pct0(r.value)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6rem',
                                color:
                                  r.above > 0.5
                                    ? theme.palette.success.main
                                    : theme.palette.error.main,
                              }}
                            >
                              SMA {r.above > 0.5 ? '▲' : '▼'} {r.z.toFixed(1)}σ
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                    <MiniTable
                      section="ML Features"
                      onAsk={onAsk}
                      headers={['Feature', 'Value']}
                      rows={[
                        [
                          'IV Rank',
                          `${num(ml_features.volatility_features.iv_rank * 100, 0)}th`,
                        ],
                        [
                          'Skew Rank',
                          `${num(ml_features.volatility_features.skew_rank * 100, 0)}th`,
                        ],
                        [
                          'Wing Steepness',
                          num(
                            ml_features.volatility_features
                              .wing_steepness_rank_d95_v_d10,
                            2,
                          ),
                        ],
                        [
                          'Put Wing Slope',
                          num(ml_features.put_wing_slope_rank, 2),
                        ],
                        [
                          'Call Wing Slope',
                          num(ml_features.call_wing_slope_rank, 2),
                        ],
                        [
                          'Convexity',
                          num(
                            ml_features.volatility_features.convexity_rank,
                            2,
                          ),
                        ],
                        ['Butterfly Rank', num(ml_features.butterfly_rank, 2)],
                        [
                          'RV / D50',
                          `${pct(ml_features.volatility_features.rv)} / ${pct(ml_features.volatility_features.d50)}`,
                        ],
                      ]}
                    />
                  </Stack>
                </Section>
              )}

              {/* SearchCandidate Rules */}
              {strategy_rules != null && (
                <Section title="Strategy Rules Advisory">
                  <Stack sx={{ gap: '14px' }}>
                    <KVTable
                      section="Strategy Rules"
                      onAsk={onAsk}
                      rows={[
                        {
                          label: 'Vol Bias',
                          value:
                            strategy_rules.vol_bias?.replace(/_/g, ' ') ??
                            'N/A',
                        },
                        {
                          label: 'Rules Fired',
                          value: `${strategy_rules.rules_fired?.length ?? 0}`,
                        },
                      ]}
                    />
                    {(
                      strategy_rules.top_fired_rules ??
                      strategy_rules.cautions ??
                      []
                    ).slice(0, 3).length > 0 && (
                      <Box sx={{ px: 0.75, py: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                        >
                          Top Rules:
                        </Typography>
                        {(
                          strategy_rules.top_fired_rules ??
                          strategy_rules.cautions ??
                          []
                        )
                          .slice(0, 3)
                          .map((rule, i) => (
                            <Typography
                              key={i}
                              variant="caption"
                              sx={{ display: 'block', fontFamily: 'monospace' }}
                            >
                              [{rule.id}] {rule.category}:{' '}
                              {rule.rationale.split('.')[0]}
                            </Typography>
                          ))}
                      </Box>
                    )}
                  </Stack>
                </Section>
              )}
            </Stack>
          </TabPanel>
        </Box>
      </TabContext>
    </Box>
  );
};

export default MoviefoneAdvisor;
